import {
    Box,
    Grid,
    Button,
    Toolbar,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Divider,
    Typography,
    MenuList,
    Avatar
} from '@mui/material';
import { Menu } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import ToolBarButton from './ToolBarButton';
import { ToolBarProps } from '../types';
import MenuItem from './MenuItem';
import { person } from 'apis';
import { useSelector } from 'react-redux';
import { RootState } from 'store/types';
import { useMe } from 'hooks/useMe';

const ToolBar = (props: ToolBarProps) => {
    const { open, federationUrl, buttons = [] } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
    const mdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [openAccordion, setOpenAccordion] = useState<boolean>(false);
    const [avatar, setAvatar] = useState<string>('');
    const { whoami } = useMe();
    const { personFunctionId, isLoggedIn } = useSelector((state: RootState) => state.auth);

    const menuSettings = {
        disableCalendar: false,
        disableAthletes: false,
        disableOfficials: false,
        disableClubs: false
    };

    const height = useMemo(() => {
        if (lgUp) {
            return open ? 45 : 55;
        }
        return 'auto';
    }, [lgUp, open]);

    const handleNavMenu = (url: string) => {
        navigate(url);
    };

    const handleNavFederation = () => {
        window.open(federationUrl, '_blank');
        window.focus();
    };

    useEffect(() => {
        const fetchAvatar = async () => {
            try {
                if (!isLoggedIn || !personFunctionId) {
                    setAvatar('');
                    return;
                }

                const result = await person.getAvatar({
                    projectTableId: 2,
                    recordId: personFunctionId
                });

                setAvatar(typeof result === 'string' ? result : '');
            } catch (error) {
                console.error(error);
                setAvatar('');
            }
        };

        fetchAvatar();
    }, [isLoggedIn, personFunctionId]);

    const getUserInitials = () => {
        const firstNameInitial = whoami?.firstName?.substring(0, 1) || '';
        const lastNameInitial = whoami?.lastName?.substring(0, 1) || '';
        const initials = `${firstNameInitial}${lastNameInitial}`.trim();

        if (initials) return initials;

        return (localStorage.getItem('loggedInUsername') || '').substring(0, 1).toUpperCase();
    };

    const checkLoginStatus = () => {
        try {
            const persistRoot = localStorage.getItem('persist:root');
            if (!persistRoot) return false;

            const rootData = JSON.parse(persistRoot);

            if (rootData.auth) {
                const authData = JSON.parse(rootData.auth);
                return authData.isLoggedIn === true;
            }

            return false;
        } catch (error) {
            console.error('Error parsing auth state:', error);
            return false;
        }
    };

    return (
        <Box
            sx={{
                height: height, // open ? 45 : 55,
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', lg: 'center' },
                display: 'flex',
                overflow: 'hidden',
                background: '#f8f9fa'
            }}
        >
            <Grid container xs={12} alignItems="center" sx={{ overflowX: 'hidden', overflowY: lgUp ? 'hidden' : 'visible' }}>
                <Grid item xs={lgUp ? 10 : 12}>
                    {lgUp ? (
                        <Toolbar sx={{ ml: 8 }}>
                            {buttons.map((button) => button?.enabled && <ToolBarButton {...button} label={t(button.label)} />)}
                        </Toolbar>
                    ) : (
                        <>
                            <Accordion
                                expanded={openAccordion}
                                onChange={() => setOpenAccordion((prev) => !prev)}
                                sx={{ width: '100%', mt: 1, background: '#f8f9fa', boxShadow: 'none' }}
                            >
                                <AccordionSummary
                                    sx={{
                                        justifyContent: 'flex-start',
                                        minHeight: 48,
                                        px: { xs: 2, sm: 3 },
                                        borderTop: 'none',
                                        '& .MuiAccordionSummary-content': {
                                            margin: 0,
                                            justifyContent: 'flex-start'
                                        },
                                        '& .MuiAccordionSummary-content.Mui-expanded': {
                                            margin: 0
                                        },
                                        background: '#f8f9fa'
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Menu fontSize="small" />
                                        <Typography fontSize="medium" fontWeight={400}>
                                            Menu
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <MenuList>
                                        {buttons.map(
                                            (button) =>
                                                button?.enabled && (
                                                    <MenuItem
                                                        {...button}
                                                        label={t(button.label)}
                                                        onClick={() => setOpenAccordion(false)}
                                                    />
                                                )
                                        )}
                                        <Divider sx={{ borderColor: '#727273' }} />
                                        <MenuItem url="/login" label={t('MemberLogin')} onClick={() => setOpenAccordion(false)} />
                                    </MenuList>
                                </AccordionDetails>
                            </Accordion>
                        </>
                    )}
                </Grid>
                {lgUp && (
                    <Grid item xs={2} container justifyContent="flex-start" alignItems="center">
                        <Button
                            variant="text"
                            sx={{ color: '#7eaaca', minWidth: 0, p: checkLoginStatus() ? 0.5 : undefined, borderRadius: '50%' }}
                            onClick={() => handleNavMenu(checkLoginStatus() ? '/home' : '/login')}
                        >
                            {checkLoginStatus() ? (
                                <Avatar
                                    src={avatar || undefined}
                                    sx={{
                                        bgcolor: '#99dfe3',
                                        color: '#673ab7d9',
                                        width: 38,
                                        height: 38,
                                        fontWeight: 800,
                                        fontSize: '1rem'
                                    }}
                                >
                                    {!avatar ? getUserInitials() : null}
                                </Avatar>
                            ) : (
                                <u>{t('MemberLogin')}</u>
                            )}
                        </Button>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default ToolBar;
